import type { ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../state/session.tsx';
import { companyHasPlanFeature } from '../../components/plan-gate.tsx';
import { LoadingBlock } from '../../components/ui.tsx';
import TeamSection from '../../components/team-section.tsx';
import { SettingsLeaf } from './settings-leaf.tsx';

export type TeamChild = 'roster' | 'saved' | 'swapPolicy';

/**
 * Settings → Employees & Teams, the roster hub: the employee list on
 * /settings/team, with Saved teams and the shift swap policy as child pages —
 * the mobile app's Employees, Saved teams and Swap policy screens.
 *
 * The whole area keeps the gate the stacked card had: `users:manage`. Swap
 * policy exists only where the staff portal does, as before. Team pay is not
 * part of the roster — it has its own gate (`reports:read` + `job_profit`) and
 * its own page — so the hub only points there.
 *
 * Each child is its own route and mounts alone, so opening the roster does
 * not load the saved-teams editor or the swap policy form.
 */
export function TeamSettingsFrame({
  current,
  anchorId,
  children,
}: {
  current: TeamChild;
  /** The id this card's wrapper had on /settings, when it had one of its own. */
  anchorId?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { company, can } = useSession();

  if (!company) return <LoadingBlock label={t('settings.loadingSettings')} />;
  if (!can('users:manage')) return <Navigate to="/settings" replace />;

  // Saved teams are crews to book visits to, so they come with scheduling —
  // the card itself renders nothing on a plan without it.
  const savedTeams = companyHasPlanFeature(company, 'scheduling');
  const swapPolicy = companyHasPlanFeature(company, 'staff_portal');
  if ((current === 'saved' && !savedTeams) || (current === 'swapPolicy' && !swapPolicy)) {
    return <Navigate to="/settings/team" replace />;
  }
  const showTeamPay = can('reports:read') && companyHasPlanFeature(company, 'job_profit');

  const tabs = [
    { to: '/settings/team', label: t('settings.teamHub.tabs.roster'), current: current === 'roster' },
    ...(savedTeams
      ? [{ to: '/settings/team/saved', label: t('settings.teamHub.tabs.saved'), current: current === 'saved' }]
      : []),
    ...(swapPolicy
      ? [
          {
            to: '/settings/team/swap-policy',
            label: t('settings.teamHub.tabs.swapPolicy'),
            current: current === 'swapPolicy',
          },
        ]
      : []),
  ];

  return (
    <SettingsLeaf
      title={t('settings.nav.sections.team')}
      tabs={tabs}
      tabsLabel={t('settings.teamHub.tabsLabel')}
      anchorId={anchorId}
    >
      {children}
      {showTeamPay ? (
        <p className="text-sm text-[color:var(--color-muted)]">
          {t('settings.teamHub.teamPayHint')}{' '}
          <Link className="font-semibold text-[color:var(--color-brand)] hover:underline" to="/settings/team-pay">
            {t('settings.teamHub.teamPayLink')}
          </Link>
        </p>
      ) : null}
    </SettingsLeaf>
  );
}

/** /settings/team: the roster. */
export default function TeamHubPage() {
  return (
    <TeamSettingsFrame current="roster" anchorId="settings-team">
      <TeamSection />
    </TeamSettingsFrame>
  );
}
